import { formatBalanceDisplay, normalizeNearZero } from './formatBalance';

const formatPercentage = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0.00%';
  }

  const normalized = normalizeNearZero(value);
  const formatted = Math.abs(normalized).toFixed(2);
  const sign = normalized > 0 ? '+' : normalized < 0 ? '-' : '';
  return `${sign}${formatted}%`;
};

const formatChartPercentage = (value, { includeSign = true } = {}) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0.00%';
  }

  const normalized = normalizeNearZero(value);
  const formatted = Math.abs(normalized).toFixed(2);

  if (!includeSign) {
    const signPrefix = normalized < 0 ? '-' : '';
    return `${signPrefix}${formatted}%`;
  }

  const sign = normalized > 0 ? '+' : normalized < 0 ? '-' : '';
  return `${sign}${formatted}%`;
};

const formatChartCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '$0';
  }

  const rounded = Math.round(value);
  const absoluteRounded = Math.abs(rounded);
  const formatted = absoluteRounded.toLocaleString('en-US');
  const prefix = rounded < 0 ? '-$' : '$';
  return `${prefix}${formatted}`;
};

export const getAccountColumnDefinitions = (type = 'account') => {
  const isPortfolioView = type === 'portfolio';

  return {
    portfolio: {
      key: 'name',
      label: isPortfolioView ? 'Portfolio' : 'Account',
      align: 'left',
      isNumeric: false
    },
    portfolioInfo: {
      key: 'portfolioInfo',
      label: isPortfolioView ? 'Account' : 'Portfolio',
      align: 'left',
      isNumeric: false
    },
    description: {
      key: 'description',
      label: 'Description',
      align: 'left',
      isNumeric: false
    },
    balance: {
      key: 'balance',
      label: 'Balance',
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    bottom_line: {
      key: 'bottom_line',
      label: 'Bottom Line',
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    cash: {
      key: 'cash',
      label: 'Cash',
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    edge: {
      key: 'edge',
      label: 'Edge (PW %)',
      chartLabel: 'Edge%',
      align: 'right',
      isNumeric: true,
      formatter: (value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return '0.00%';
        }
        return `${value > 0 ? '+' : value < 0 ? '-' : ''}${Math.abs(value).toFixed(2)}%`;
      },
      chartFormatter: (value) => formatChartPercentage(value, { includeSign: false }),
      valueType: 'percentage'
    },
    today_gain_amount: {
      key: 'today_gain_amount',
      label: "Today's Gain ($)",
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    today_gain_percentage: {
      key: 'today_gain_percentage',
      label: "Today's Gain (%)",
      align: 'right',
      isNumeric: true,
      formatter: formatPercentage,
      chartFormatter: (value) => formatChartPercentage(value, { includeSign: false }),
      valueType: 'percentage'
    },
    total_gain_amount: {
      key: 'total_gain_amount',
      label: 'Total Gain ($)',
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    total_gain_percentage: {
      key: 'total_gain_percentage',
      label: 'Total Gain (%)',
      align: 'right',
      isNumeric: true,
      formatter: formatPercentage,
      chartFormatter: (value) => formatChartPercentage(value, { includeSign: false }),
      valueType: 'percentage'
    },
    ytd_gain_amount: {
      key: 'ytd_gain_amount',
      label: 'YTD Gain ($)',
      align: 'right',
      isNumeric: true,
      formatter: formatBalanceDisplay,
      chartFormatter: formatChartCurrency,
      valueType: 'currency'
    },
    ytd_gain_percentage: {
      key: 'ytd_gain_percentage',
      label: 'YTD Gain (%)',
      align: 'right',
      isNumeric: true,
      formatter: formatPercentage,
      chartFormatter: (value) => formatChartPercentage(value, { includeSign: false }),
      valueType: 'percentage'
    }
  };
};

export const ACCOUNT_TABLE_DEFAULT_VISIBLE_COLUMNS = {
  portfolio: true,
  description: true,
  balance: true,
  bottom_line: true,
  cash: true,
  edge: true,
  today_gain_amount: true,
  today_gain_percentage: true,
  total_gain_amount: true,
  total_gain_percentage: true,
  ytd_gain_amount: true,
  ytd_gain_percentage: true,
  portfolioInfo: false
};

export const ACCOUNT_TABLE_DEFAULT_COLUMN_ORDER = [
  'portfolio',
  'description',
  'balance',
  'bottom_line',
  'cash',
  'edge',
  'today_gain_amount',
  'today_gain_percentage',
  'total_gain_amount',
  'total_gain_percentage',
  'ytd_gain_amount',
  'ytd_gain_percentage',
  'portfolioInfo'
];

export const ACCOUNT_NUMERIC_COLUMN_KEYS = new Set([
  'balance',
  'bottom_line',
  'cash',
  'edge',
  'today_gain_amount',
  'today_gain_percentage',
  'total_gain_amount',
  'total_gain_percentage',
  'ytd_gain_amount',
  'ytd_gain_percentage'
]);

export const getAccountColumnLabel = (columnKey, type = 'account') => {
  const definitions = getAccountColumnDefinitions(type);
  return definitions[columnKey]?.label || columnKey;
};

export const getAccountColumnAlignment = (columnKey, type = 'account') => {
  const definitions = getAccountColumnDefinitions(type);
  return definitions[columnKey]?.align || 'left';
};

export const formatAccountColumnValue = (columnKey, value, type = 'account') => {
  const definitions = getAccountColumnDefinitions(type);
  const formatter = definitions[columnKey]?.formatter;
  if (formatter) {
    return formatter(value);
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return value ?? '';
};

const resolveColumnValueType = (columnKey, columnDefinition) => {
  if (columnDefinition?.valueType) {
    return columnDefinition.valueType;
  }

  const label = columnDefinition?.chartLabel || columnDefinition?.label || '';

  if (label.includes('%')) {
    return 'percentage';
  }

  if (label.includes('$')) {
    return 'currency';
  }

  if (typeof columnKey === 'string') {
    if (/_percentage$/.test(columnKey) || /_pct$/.test(columnKey) || /_percent$/.test(columnKey)) {
      return 'percentage';
    }

    if (/_amount$/.test(columnKey) || /(balance|bottom_line|cash)$/.test(columnKey)) {
      return 'currency';
    }
  }

  return undefined;
};

export const formatAccountChartValue = (columnKey, value, type = 'account') => {
  const definitions = getAccountColumnDefinitions(type);
  const columnDefinition = definitions[columnKey];
  const formatter = columnDefinition?.formatter;
  const chartFormatter = columnDefinition?.chartFormatter;
  const valueType = resolveColumnValueType(columnKey, columnDefinition);

  if (chartFormatter) {
    return chartFormatter(value);
  }

  if (valueType === 'percentage') {
    return formatChartPercentage(value, { includeSign: false });
  }

  if (valueType === 'currency') {
    return formatChartCurrency(value);
  }

  if (formatter === formatBalanceDisplay) {
    return formatChartCurrency(value);
  }

  if (formatter) {
    return formatter(value);
  }

  if (typeof columnKey === 'string' && /_percentage$/.test(columnKey)) {
    return formatChartPercentage(value, { includeSign: false });
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value.toLocaleString();
  }

  return value ?? '';
};

export const getAccountNumericValue = (account, columnKey, { accountEdgeMap } = {}) => {
  if (!account || typeof account !== 'object') {
    return null;
  }

  switch (columnKey) {
    case 'edge': {
      if (accountEdgeMap && account.account_id && typeof accountEdgeMap[account.account_id] === 'number') {
        return normalizeNearZero(accountEdgeMap[account.account_id]);
      }
      if (typeof account.expectancy === 'number') {
        return normalizeNearZero(account.expectancy);
      }
      return null;
    }
    default: {
      const rawValue = account[columnKey];
      if (typeof rawValue !== 'number' || Number.isNaN(rawValue)) {
        return null;
      }
      return normalizeNearZero(rawValue);
    }
  }
};

export const isAccountNumericColumn = (columnKey) => ACCOUNT_NUMERIC_COLUMN_KEYS.has(columnKey);
